<template>
    <component
        :is="disabled ? 'div' : 'OverlayScrollbarsComponent'"
        ref="scrollContainer"
        element="div"
        :options="options"
        :class="['scroll-container', { inverted: invertScroll }]"
        defer
        @os-scroll="onScroll"
        @os-updated="onUpdate"
        @os-initialized="setOverflow"
    >
        <!-- @slot scrollable content -->
        <slot :overflow="overflow" />
    </component>
</template>

<script lang="ts">
import { type PropType } from 'vue';
import 'overlayscrollbars/overlayscrollbars.css';
import type { OverflowBehavior, OverlayScrollbars, OnUpdatedEventListenerArgs } from 'overlayscrollbars';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-vue';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
import { smoothScroll } from '@/helpers/dom';

export const AXIS = {
    X: 'x',
    Y: 'y',
    BOTH: 'both',
    NONE: 'none',
};

/**
 * Scroll Container that applies overlayed scrollbars to the content
 */
export default {
    name: 'ScrollContainer',
    components: {
        OverlayScrollbarsComponent,
    },
    props: {
        /**
         * Specifies the overflow for the x axis
         * @values hidden, visible, scroll, visible-hidden, visible-scroll
         */
        overflowX: {
            type: String as PropType<OverflowBehavior>,
            default: 'scroll',
        },
        /**
         * Specifies the overflow for the y axis
         * @values hidden, visible, scroll, visible-hidden, visible-scroll
         */
        overflowY: {
            type: String as PropType<OverflowBehavior>,
            default: 'scroll',
        },
        /**
         * Disables any scroll related behavior and just renders a div element
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether the scrollbars should auto-hide when the focus is gone
         */
        scrollbarsAutoHide: {
            type: Boolean,
            default: false,
        },
        /**
         * Inverts the scroll direction from top-down to bottom-up.
         * As this also inverts the content flow, you might want to wrap your content with another div.
         * Custom scrollbar of ScrollContainer has position & direction issue when inverted, setting this to true
         * will hide the custom scrollbar & native browser scrollbar appears instead.
         */
        invertScroll: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['change:overflow', 'scroll'],
    data: () => ({
        overflow: {
            x: 0,
            y: 0,
        },
    }),
    computed: {
        options() {
            return {
                overflow: {
                    x: this.overflowX,
                    y: this.overflowY,
                },
                scrollbars: {
                    autoHide: this.scrollbarsAutoHide ? 'leave' : 'never',
                },
            };
        },
    },
    watch: {
        disable() {
            const instance = this.getScrollInstance();
            if (!instance) return;

            this.setOverflow(instance);
        },
    },
    methods: {
        getScrollInstance() {
            const ref = this.$refs.scrollContainer as OverlayScrollbarsComponentRef;
            return ref.osInstance();
        },
        /**
         * Scrolls the container to the given position { top, left }
         * @public
         */
        scroll(pos: Exclude<ScrollToOptions, 'behavior'>): Promise<void> {
            const el = this.getScrollElem();
            if (!el) throw new Error('Scroll element not found');

            return smoothScroll(el, pos);
        },
        /**
         * Returns the html element that can be scrolled if the scroll container is not disabled
         * @public
         */
        getScrollElem(): HTMLElement | undefined {
            if (this.disabled) return undefined;

            return this.getScrollInstance()?.elements().content;
        },
        setOverflow(instance: OverlayScrollbars) {
            const overflow = instance.state().overflowAmount;
            this.overflow = overflow && !this.disabled ? overflow : { x: 0, y: 0 };
        },
        onScroll(instance: OverlayScrollbars, event: Event) {
            /**
             * Forwards native scroll event
             */
            this.$emit('scroll', event);
        },
        onUpdate(instance: OverlayScrollbars, args: OnUpdatedEventListenerArgs) {
            if (!args.updateHints.overflowAmountChanged) return;

            this.setOverflow(instance);
            /**
             * Triggered whenever the overflow of the content changes
             * @property {{x: number, y: number}} overflow overflow amount for each axis
             */
            this.$emit('change:overflow', this.overflow);
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.os-scrollbar) {
    --os-size: 10px;
    --os-handle-bg: theme('colors.blue.400');
    --os-handle-bg-hover: theme('colors.blue.400');
    --os-handle-bg-active: theme('colors.blue.400');
}

.inverted {
    :deep([data-overlayscrollbars-contents]) {
        @apply custom-scrollbar flex flex-col-reverse;
        scrollbar-width: thin !important;
    }

    :deep(.os-scrollbar-track) {
        @apply hidden;
    }
}
</style>
